import { useState } from 'react';
import { 
  Calculator, 
  Battery,
  Zap,
  Volume2,
  Ruler,
  TrendingUp,
  Download,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react';

interface CalculationResult {
  value: number;
  unit: string;
  status: 'success' | 'warning' | 'error';
  message: string;
}

export function CustomCalculationsPage() {
  // Battery Backup Calculator State
  const [standbyLoad, setStandbyLoad] = useState('');
  const [alarmLoad, setAlarmLoad] = useState('');
  const [standbyTime, setStandbyTime] = useState('24');
  const [alarmTime, setAlarmTime] = useState('5');
  const [batteryResult, setBatteryResult] = useState<CalculationResult | null>(null);

  // Voltage Drop Calculator State
  const [wireLength, setWireLength] = useState('');
  const [wireCurrent, setWireCurrent] = useState('');
  const [wireGauge, setWireGauge] = useState('18');
  const [voltageDropResult, setVoltageDropResult] = useState<CalculationResult | null>(null);

  // Speaker Coverage Calculator State
  const [roomLength, setRoomLength] = useState('');
  const [roomWidth, setRoomWidth] = useState('');
  const [ceilingHeight, setCeilingHeight] = useState('');
  const [ambientNoise, setAmbientNoise] = useState('55');
  const [speakerResult, setSpeakerResult] = useState<CalculationResult | null>(null);

  // Detector Spacing Calculator State
  const [detectorRoomLength, setDetectorRoomLength] = useState('');
  const [detectorRoomWidth, setDetectorRoomWidth] = useState('');
  const [detectorCeilingHeight, setDetectorCeilingHeight] = useState('');
  const [ceilingType, setCeilingType] = useState('smooth');
  const [detectorResult, setDetectorResult] = useState<CalculationResult | null>(null);

  // Wire Gauge Resistance (ohms per 1000 ft)
  const wireResistance: { [key: string]: number } = {
    '18': 6.39,
    '16': 4.02,
    '14': 2.53,
    '12': 1.59,
    '10': 1.00
  };

  const calculateBatteryBackup = () => {
    const standby = parseFloat(standbyLoad);
    const alarm = parseFloat(alarmLoad);
    const standbyHours = parseFloat(standbyTime);
    const alarmMinutes = parseFloat(alarmTime);

    if (isNaN(standby) || isNaN(alarm) || standby < 0 || alarm < 0) {
      setBatteryResult({
        value: 0,
        unit: 'Ah',
        status: 'error',
        message: 'Please enter valid positive numbers for all fields'
      });
      return;
    }

    // Calculate total amp-hours needed
    const standbyAh = standby * standbyHours;
    const alarmAh = alarm * (alarmMinutes / 60);
    const totalAh = standbyAh + alarmAh;

    // Apply 20% safety factor
    const requiredAh = totalAh * 1.2;

    let status: 'success' | 'warning' | 'error' = 'success';
    let message = 'Battery capacity calculated with 20% safety factor';

    if (requiredAh > 100) {
      status = 'warning';
      message = 'Large battery capacity required. Consider multiple batteries or reducing load.';
    } else if (requiredAh < 7) {
      status = 'warning';
      message = 'Minimum recommended battery size is 7Ah for reliability.';
    }

    setBatteryResult({
      value: Math.ceil(requiredAh * 10) / 10,
      unit: 'Ah',
      status,
      message
    });
  };

  const calculateVoltageDrop = () => {
    const length = parseFloat(wireLength);
    const current = parseFloat(wireCurrent);
    const resistance = wireResistance[wireGauge];

    if (isNaN(length) || isNaN(current) || length <= 0 || current <= 0) {
      setVoltageDropResult({
        value: 0,
        unit: 'V',
        status: 'error',
        message: 'Please enter valid positive numbers for length and current'
      });
      return;
    }

    // Calculate voltage drop: V = 2 * I * R * L / 1000
    // (2 for round trip, R in ohms per 1000ft)
    const voltageDrop = (2 * current * resistance * length) / 1000;

    let status: 'success' | 'warning' | 'error' = 'success';
    let message = 'Voltage drop is within acceptable limits';

    if (voltageDrop > 3.0) {
      status = 'error';
      message = 'Voltage drop exceeds 3V! Increase wire gauge or reduce circuit length.';
    } else if (voltageDrop > 2.0) {
      status = 'warning';
      message = 'Voltage drop is high. Consider increasing wire gauge for better performance.';
    }

    setVoltageDropResult({
      value: Math.round(voltageDrop * 100) / 100,
      unit: 'V',
      status,
      message
    });
  };

  const calculateSpeakerCoverage = () => {
    const length = parseFloat(roomLength);
    const width = parseFloat(roomWidth);
    const height = parseFloat(ceilingHeight);
    const ambient = parseFloat(ambientNoise);

    if (isNaN(length) || isNaN(width) || isNaN(height) || length <= 0 || width <= 0 || height <= 0) {
      setSpeakerResult({
        value: 0,
        unit: 'speakers',
        status: 'error',
        message: 'Please enter valid positive numbers for all dimensions'
      });
      return;
    }

    const area = length * width;
    
    // Typical speaker coverage: 1 speaker per 2500 sq ft for standard ceiling height
    // Adjust for ceiling height and ambient noise
    let coverageArea = 2500;
    
    if (height > 12) {
      coverageArea = coverageArea * 0.7; // Reduce coverage for high ceilings
    } else if (height < 9) {
      coverageArea = coverageArea * 1.2; // Increase coverage for low ceilings
    }

    if (ambient > 70) {
      coverageArea = coverageArea * 0.6; // Reduce coverage for high ambient noise
    } else if (ambient > 60) {
      coverageArea = coverageArea * 0.8;
    }

    const speakersNeeded = Math.ceil(area / coverageArea);

    let status: 'success' | 'warning' | 'error' = 'success';
    let message = `Coverage: ${Math.round(area / speakersNeeded)} sq ft per speaker`;

    if (ambient > 75) {
      status = 'warning';
      message += '. High ambient noise may require additional speakers or higher wattage.';
    }

    setSpeakerResult({
      value: speakersNeeded,
      unit: 'speakers',
      status,
      message
    });
  };

  const calculateDetectorSpacing = () => {
    const length = parseFloat(detectorRoomLength);
    const width = parseFloat(detectorRoomWidth);
    const height = parseFloat(detectorCeilingHeight);

    if (isNaN(length) || isNaN(width) || isNaN(height) || length <= 0 || width <= 0 || height <= 0) {
      setDetectorResult({
        value: 0,
        unit: 'detectors',
        status: 'error',
        message: 'Please enter valid positive numbers for all dimensions'
      });
      return;
    }

    const area = length * width;
    
    // NFPA 72: Smooth ceiling = 900 sq ft per detector (30ft spacing)
    // Beam construction or irregular = 600 sq ft per detector
    let coverageArea = ceilingType === 'smooth' ? 900 : 600;
    
    // Adjust for ceiling height
    if (height > 30) {
      coverageArea = coverageArea * 0.5; // High ceilings require more detectors
    } else if (height > 20) {
      coverageArea = coverageArea * 0.7;
    } else if (height > 15) {
      coverageArea = coverageArea * 0.85;
    }

    const detectorsNeeded = Math.ceil(area / coverageArea);

    let status: 'success' | 'warning' | 'error' = 'success';
    let message = `Coverage: ${Math.round(area / detectorsNeeded)} sq ft per detector (NFPA 72 compliant)`;

    if (height > 30) {
      status = 'warning';
      message += '. High ceilings may require beam detectors instead of spot-type.';
    }

    setDetectorResult({
      value: detectorsNeeded,
      unit: 'detectors',
      status,
      message
    });
  };

  const exportCalculations = () => {
    const calculations = {
      batteryBackup: batteryResult,
      voltageDrop: voltageDropResult,
      speakerCoverage: speakerResult,
      detectorSpacing: detectorResult,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(calculations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fire-alarm-calculations.json';
    link.click();
  };

  const renderResult = (result: CalculationResult | null) => {
    if (!result) return null;

    const statusColors = {
      success: 'bg-green-50 border-green-200 text-green-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800'
    };

    const statusIcons = {
      success: <CheckCircle2 className="w-5 h-5 text-green-600" />,
      warning: <AlertCircle className="w-5 h-5 text-yellow-600" />,
      error: <AlertCircle className="w-5 h-5 text-red-600" />
    };

    return (
      <div className={`mt-4 p-4 border rounded-lg ${statusColors[result.status]}`}>
        <div className="flex items-start">
          {statusIcons[result.status]}
          <div className="ml-3 flex-1">
            <div className="text-2xl font-bold mb-1">
              {result.value} {result.unit}
            </div>
            <p className="text-sm">{result.message}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Calculator className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Custom Calculations</h1>
            <p className="text-xl text-green-100">
              Professional fire alarm system calculations and sizing tools
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Export Button */}
        <div className="mb-8 flex justify-end">
          <button
            onClick={exportCalculations}
            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-200"
          >
            <Download className="w-5 h-5 mr-2" />
            Export All Results
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Battery Backup Calculator */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-green-100 rounded-lg mr-3">
                <Battery className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Battery Backup Calculator</h2>
                <p className="text-sm text-slate-600">Calculate required battery capacity (NFPA 72 Ch. 23)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Standby Load (Amps)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={standbyLoad}
                  onChange={(e) => setStandbyLoad(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Alarm Load (Amps)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={alarmLoad}
                  onChange={(e) => setAlarmLoad(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="2.50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Standby Time (Hours)
                  </label>
                  <input
                    type="number"
                    value={standbyTime}
                    onChange={(e) => setStandbyTime(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Alarm Time (Minutes)
                  </label>
                  <input
                    type="number"
                    value={alarmTime}
                    onChange={(e) => setAlarmTime(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={calculateBatteryBackup}
                className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Calculate Battery Capacity
              </button>

              {renderResult(batteryResult)}
            </div>
          </div>

          {/* Voltage Drop Calculator */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-yellow-100 rounded-lg mr-3">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Voltage Drop Calculator</h2>
                <p className="text-sm text-slate-600">Calculate voltage drop for NAC circuits (NEC 760)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Wire Length (feet)
                </label>
                <input
                  type="number"
                  value={wireLength}
                  onChange={(e) => setWireLength(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Current Draw (Amps)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={wireCurrent}
                  onChange={(e) => setWireCurrent(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="1.50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Wire Gauge (AWG)
                </label>
                <select
                  value={wireGauge}
                  onChange={(e) => setWireGauge(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="18">18 AWG</option>
                  <option value="16">16 AWG</option>
                  <option value="14">14 AWG</option>
                  <option value="12">12 AWG</option>
                  <option value="10">10 AWG</option>
                </select>
              </div>

              <button
                onClick={calculateVoltageDrop}
                className="w-full py-3 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Calculate Voltage Drop
              </button>

              {renderResult(voltageDropResult)}
            </div>
          </div>

          {/* Speaker Coverage Calculator */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-purple-100 rounded-lg mr-3">
                <Volume2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Speaker Coverage Calculator</h2>
                <p className="text-sm text-slate-600">Calculate speaker quantity for voice evacuation (NFPA 72 Ch. 18)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Room Length (ft)
                  </label>
                  <input
                    type="number"
                    value={roomLength}
                    onChange={(e) => setRoomLength(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Room Width (ft)
                  </label>
                  <input
                    type="number"
                    value={roomWidth}
                    onChange={(e) => setRoomWidth(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="80"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ceiling Height (ft)
                </label>
                <input
                  type="number"
                  value={ceilingHeight}
                  onChange={(e) => setCeilingHeight(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ambient Noise Level (dBA)
                </label>
                <input
                  type="number"
                  value={ambientNoise}
                  onChange={(e) => setAmbientNoise(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={calculateSpeakerCoverage}
                className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Calculate Speaker Count
              </button>

              {renderResult(speakerResult)}
            </div>
          </div>

          {/* Detector Spacing Calculator */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-blue-100 rounded-lg mr-3">
                <Ruler className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Detector Spacing Calculator</h2>
                <p className="text-sm text-slate-600">Calculate smoke detector quantity (NFPA 72 Ch. 17)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Room Length (ft)
                  </label>
                  <input
                    type="number"
                    value={detectorRoomLength}
                    onChange={(e) => setDetectorRoomLength(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Room Width (ft)
                  </label>
                  <input
                    type="number"
                    value={detectorRoomWidth}
                    onChange={(e) => setDetectorRoomWidth(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="80"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ceiling Height (ft)
                </label>
                <input
                  type="number"
                  value={detectorCeilingHeight}
                  onChange={(e) => setDetectorCeilingHeight(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ceiling Type
                </label>
                <select
                  value={ceilingType}
                  onChange={(e) => setCeilingType(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="smooth">Smooth Ceiling</option>
                  <option value="beam">Beam Construction / Irregular</option>
                </select>
              </div>

              <button
                onClick={calculateDetectorSpacing}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Calculate Detector Count
              </button>

              {renderResult(detectorResult)}
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="mt-12 bg-gradient-to-br from-green-600 to-teal-600 text-white rounded-xl shadow-lg p-8">
          <div className="flex items-center mb-4">
            <Info className="w-6 h-6 mr-2" />
            <h2 className="text-2xl font-bold">Important Notes</h2>
          </div>
          <ul className="space-y-2 text-green-100">
            <li className="flex items-start">
              <TrendingUp className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>All calculations include appropriate safety factors per NFPA 72 requirements</span>
            </li>
            <li className="flex items-start">
              <TrendingUp className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>Results should be verified by a licensed fire alarm engineer</span>
            </li>
            <li className="flex items-start">
              <TrendingUp className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>Local amendments to codes may require different calculations</span>
            </li>
            <li className="flex items-start">
              <TrendingUp className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>Manufacturer specifications should always be consulted for specific equipment</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
